import { Undo2Icon } from "lucide-react";
import {
  createContext,
  type ComponentPropsWithoutRef,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import { COARSE_POINTER_HIT_AREA_CLASS } from "~/lib/touchTarget";
import { cn } from "../../lib/utils";
import {
  SETTINGS_PAGE_SCROLL_CLASS,
  SETTINGS_PAGE_STACK_CLASS,
  SETTINGS_ROW_CONTROL_CLASS,
} from "./settingsControlDensity";
import { scrollAndFocusSettingsTarget } from "./settingsAnchorScroll";
import { SettingsScopeSentence } from "./SettingsScopeSentence";
import { Button } from "../ui/button";
import { Tooltip, TooltipPopup, TooltipTrigger } from "../ui/tooltip";
import { hashWithoutTarget, settingsTargetFromHash, useLocationHash } from "./settingsTargetHash";

/**
 * The settings design standard, ported from t3code's `settingsLayout.tsx`.
 *
 * Every settings surface composes from these four: a page container, sections
 * inside it, rows inside those, and a reset affordance on a row. Porting the
 * set rather than each page's own chrome is what makes the pages agree —
 * before this, a page's density and heading scale were whatever its author
 * chose, and Diagnostics had built an entirely separate primitive family.
 *
 * Forge's button vocabulary is used throughout (`size="icon-xs"`,
 * `variant="ghost"`); upstream's `icon-micro` / `ghost-muted` do not exist here
 * and inventing them would fork the button scale to match a borrowed file.
 */

interface SettingsSearchTargetContextValue {
  readonly targetId: string | null;
  readonly onTargetHandled: () => void;
}

const noop = () => undefined;
const SettingsSearchTargetContext = createContext<SettingsSearchTargetContextValue>({
  targetId: null,
  onTargetHandled: noop,
});

export function SettingsSearchTargetProvider({
  targetId,
  onTargetHandled = noop,
  children,
}: {
  readonly targetId: string | null;
  readonly onTargetHandled?: () => void;
  readonly children: ReactNode;
}) {
  const value = useMemo(() => ({ targetId, onTargetHandled }), [onTargetHandled, targetId]);
  return <SettingsSearchTargetContext value={value}>{children}</SettingsSearchTargetContext>;
}

function useSettingsSearchTarget<T extends HTMLElement>(id: string | undefined) {
  const { targetId, onTargetHandled } = useContext(SettingsSearchTargetContext);
  const isSearchTarget = id !== undefined && id === targetId;
  return useCallback(
    (target: T | null) => {
      if (target && isSearchTarget) {
        // Clear the hash only once the jump has landed. Clearing it up front
        // removed the browser's own fragment handling as a fallback, so a jump
        // that failed had nothing left to finish it.
        scrollAndFocusSettingsTarget(target, onTargetHandled);
      }
    },
    [isSearchTarget, onTargetHandled],
  );
}

/** Re-render every `intervalMs`; return a stable timestamp snapshot for render-time relative labels. */
export function useRelativeTimeTick(intervalMs = 1_000) {
  const [nowMs, setNowMs] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNowMs(Date.now()), intervalMs);
    return () => clearInterval(id);
  }, [intervalMs]);
  return nowMs;
}

export function SettingsSection({
  title,
  icon,
  headerAction,
  children,
  className,
  ...sectionProps
}: ComponentPropsWithoutRef<"section"> & {
  readonly title: string;
  readonly icon?: ReactNode;
  readonly headerAction?: ReactNode;
  readonly children: ReactNode;
}) {
  const targetRef = useSettingsSearchTarget<HTMLElement>(sectionProps.id);

  return (
    <section
      {...sectionProps}
      ref={targetRef}
      tabIndex={sectionProps.id ? -1 : sectionProps.tabIndex}
      className={cn("space-y-3", className)}
    >
      <div className="flex min-h-8 items-center justify-between gap-4 px-3 sm:px-4">
        <h2 className="flex items-center gap-2 text-lg font-semibold tracking-[-0.025em] text-foreground">
          {icon}
          {title}
        </h2>
        <div className="flex min-h-7 min-w-7 items-center justify-end">{headerAction}</div>
      </div>
      <div className="relative space-y-1 overflow-visible text-foreground">{children}</div>
    </section>
  );
}

export function SettingsRow({
  title,
  description,
  status,
  resetAction,
  control,
  children,
  className,
  ...rowProps
}: Omit<ComponentPropsWithoutRef<"div">, "title"> & {
  readonly title: ReactNode;
  readonly description?: ReactNode;
  readonly status?: ReactNode;
  readonly resetAction?: ReactNode;
  readonly control?: ReactNode;
  readonly children?: ReactNode;
}) {
  const targetRef = useSettingsSearchTarget<HTMLDivElement>(rowProps.id);

  return (
    <div
      {...rowProps}
      ref={targetRef}
      tabIndex={rowProps.id ? -1 : rowProps.tabIndex}
      className={cn("rounded-xl px-3 sm:px-4", children ? "pt-3 pb-1" : "py-3", className)}
    >
      {/* A shared second column, not a `justify-between`: controls line up down
          the page instead of tracking each row's label length. */}
      <div className="flex flex-col gap-3 sm:grid sm:grid-cols-[minmax(0,1fr)_minmax(10rem,auto)] sm:items-center sm:gap-8">
        <div className="min-w-0 flex-1 space-y-1">
          <div className="flex min-h-5 items-center gap-1.5">
            <h3 className="text-sm font-medium tracking-[-0.005em] text-foreground">{title}</h3>
            <span className="inline-flex h-5 w-5 shrink-0 items-center justify-center">
              {resetAction}
            </span>
          </div>
          {description ? (
            <p className="max-w-xl text-[13px] leading-[1.45] text-muted-foreground/80">
              {description}
            </p>
          ) : null}
          {status ? <div className="pt-0.5 text-xs text-muted-foreground">{status}</div> : null}
        </div>
        {control ? (
          <div
            // Names the control cell so the settings design system's one
            // load-bearing geometry property — controls sharing a right edge
            // across rows — can be asserted against the real stylesheet.
            data-slot="settings-row-control"
            className={SETTINGS_ROW_CONTROL_CLASS}
          >
            {control}
          </div>
        ) : null}
      </div>
      {children}
    </div>
  );
}

/**
 * A setting whose control is a full-width block, not a right-hand control.
 *
 * `SettingsRow`'s second column is `minmax(10rem, auto)` — that `auto` is
 * max-content — so a grid of tiles placed there is sized by the widest caption
 * it happens to carry rather than by the row. The colour-scheme picker's three
 * one-word captions asked for ~208px, so it rendered as a strip pinned to the
 * right edge while the theme grid beneath it ran the section's whole 896px
 * column: two grids of the same tile, at two different widths, on one page.
 * `w-full` does not save it — full width of an intrinsically-sized track is
 * still that track's intrinsic width.
 *
 * So this is the other shape a setting comes in, and the reference's own
 * (`ThemeLibrary` in t3code's `ThemeSettings.tsx`): label above, control
 * spanning the content column below. It carries `SettingsRow`'s label,
 * description and reset chrome verbatim, because the two must read as the same
 * page — the only thing that differs is where the control goes.
 */
export function SettingsBlock({
  title,
  description,
  resetAction,
  children,
  className,
  ...blockProps
}: Omit<ComponentPropsWithoutRef<"div">, "title"> & {
  readonly title: ReactNode;
  readonly description?: ReactNode;
  readonly resetAction?: ReactNode;
  readonly children: ReactNode;
}) {
  const targetRef = useSettingsSearchTarget<HTMLDivElement>(blockProps.id);

  return (
    <div
      {...blockProps}
      ref={targetRef}
      tabIndex={blockProps.id ? -1 : blockProps.tabIndex}
      // The same padding as `SettingsRow`, so a block's label and its control
      // sit on the section's left edge rather than a second one a few pixels
      // over. Upstream additionally wraps each grid in `mx-auto max-w-[56rem]`;
      // here `SettingsPageContainer` is already `max-w-4xl`, which *is* 56rem,
      // and nesting the cap again would only make it drift the day either moves.
      className={cn("space-y-2 rounded-xl px-3 py-3 sm:px-4", className)}
    >
      <div className="flex min-h-5 items-center gap-1.5">
        <h3 className="font-medium text-foreground text-sm tracking-[-0.005em]">{title}</h3>
        <span className="inline-flex h-5 w-5 shrink-0 items-center justify-center">
          {resetAction}
        </span>
      </div>
      {description ? (
        <p className="max-w-xl text-[13px] text-muted-foreground/80 leading-[1.45]">
          {description}
        </p>
      ) : null}
      {children}
    </div>
  );
}

export function SettingResetButton({
  label,
  disabled = false,
  onClick,
}: {
  readonly label: string;
  readonly disabled?: boolean;
  readonly onClick: () => void;
}) {
  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <Button
            size="icon-xs"
            variant="ghost"
            aria-label={`Reset ${label} to default`}
            disabled={disabled}
            // Stays 32px so it does not shout next to the label it resets,
            // and takes the 48px minimum as an invisible pad on coarse
            // pointers only. Growing the visible control instead would put a
            // reset affordance on equal footing with the setting itself.
            className={cn(
              "size-8 rounded-lg p-0 text-muted-foreground hover:text-foreground sm:size-5 sm:rounded-sm",
              COARSE_POINTER_HIT_AREA_CLASS,
            )}
            onClick={(event) => {
              event.stopPropagation();
              onClick();
            }}
          >
            <Undo2Icon className="size-3" />
          </Button>
        }
      />
      <TooltipPopup side="top">Reset to default</TooltipPopup>
    </Tooltip>
  );
}

export function SettingsPageContainer({
  children,
  className,
}: {
  readonly children: ReactNode;
  readonly className?: string;
}) {
  const hash = useLocationHash();
  const targetId = settingsTargetFromHash(hash);
  // Clear the hash once the jump lands, so re-navigating to the same row
  // fires again rather than being a no-op against an unchanged URL.
  const clearTargetHash = useCallback(() => {
    const location = globalThis.window?.location;
    if (!location) return;
    window.history.replaceState(
      // NOT null. TanStack Router keeps its entry key/index in
      // `window.history.state`, and blanking it leaves this settings entry
      // without the metadata used for history reconciliation, blockers and
      // scroll restoration -- so Back/Forward onto it misbehaves later, long
      // after the jump that caused it.
      window.history.state,
      "",
      `${location.pathname}${location.search}${hashWithoutTarget(location.hash)}`,
    );
  }, []);

  return (
    <SettingsSearchTargetProvider targetId={targetId} onTargetHandled={clearTargetHash}>
      <div className={SETTINGS_PAGE_SCROLL_CLASS} data-settings-page-scroll>
        <div className={cn(SETTINGS_PAGE_STACK_CLASS, className)}>
          {/* The scope sentence leads every scoped page; device-only pages
              hide it themselves, so the container stays ignorant of which is
              which. Null outside the settings route, which keeps the suites
              that mount panels standalone rendering exactly as before. */}
          <SettingsScopeSentence />
          {children}
        </div>
      </div>
    </SettingsSearchTargetProvider>
  );
}

/** Jump to a settings row or section by id. False when it is not mounted. */
export function scrollToSettingsTarget(targetId: string): boolean {
  const target = document.getElementById(targetId);
  if (!target) return false;
  scrollAndFocusSettingsTarget(target);
  return true;
}
